/**
 * Printer registry — localStorage-backed, DESIGNER-LOCAL.
 *
 * Deliberately NOT on the Host Contract / adapter seam.
 * Printers are deployment-local config, not authoring resources.
 * This module is the deletable seam when a future server-side executor
 * takes over.
 */
import { useCallback, useEffect, useState } from 'react'
import { z } from 'zod'

export const STORAGE_KEY = 'ld:printers'

export const SavedPrinterSchema = z.object({
  id: z.string(),
  name: z.string(),
  host: z.string(),
  port: z.number().int().min(1).max(65535).default(9100),
  profileId: z.string(),
  createdAt: z.string(),
  // Auto-detected from the printer's ~HI reply on "Test connection".
  // Optional for backward-compat: records written before these fields
  // existed parse unchanged.
  detectedDpi: z.number().optional(),
  detectedModel: z.string().optional(),
})

export type SavedPrinter = z.infer<typeof SavedPrinterSchema>

// ---------------------------------------------------------------------------
// Storage helpers
// ---------------------------------------------------------------------------

/** Read all valid printers from localStorage. Corrupt/invalid data → empty list. */
export function listPrinters(): SavedPrinter[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    const result: SavedPrinter[] = []
    for (const item of parsed) {
      const r = SavedPrinterSchema.safeParse(item)
      if (r.success) result.push(r.data)
      // Invalid individual entries are silently dropped
    }
    return result
  } catch {
    return []
  }
}

/** Upsert a printer by id. */
export function savePrinter(printer: SavedPrinter): void {
  const current = listPrinters()
  const idx = current.findIndex(p => p.id === printer.id)
  if (idx >= 0) {
    current[idx] = printer
  } else {
    current.push(printer)
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(current))
}

/** Delete a printer by id. No-op if not found. */
export function deletePrinter(id: string): void {
  const current = listPrinters().filter(p => p.id !== id)
  localStorage.setItem(STORAGE_KEY, JSON.stringify(current))
}

// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------

export interface UsePrintersResult {
  printers: SavedPrinter[]
  save: (printer: SavedPrinter) => void
  remove: (id: string) => void
}

export function usePrinters(): UsePrintersResult {
  const [printers, setPrinters] = useState<SavedPrinter[]>(() => listPrinters())

  // Sync in case another tab mutates storage (or tests seed it before mount).
  useEffect(() => {
    setPrinters(listPrinters())
  }, [])

  const save = useCallback((printer: SavedPrinter) => {
    savePrinter(printer)
    setPrinters(listPrinters())
  }, [])

  const remove = useCallback((id: string) => {
    deletePrinter(id)
    setPrinters(listPrinters())
  }, [])

  return { printers, save, remove }
}
